import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useIsSearchToFeedEnabled } from '@modules/common/hooks';
import {
  useSearchAnalysesWhereClause,
  useUpdateSearchDashboard,
} from '@modules/feed/hooks';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { getSearchMetadataUrl } from '@modules/feed/utils';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import { EQLProps, ExpressionType } from 'v2/core/repository/eql/types';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useGetUser } from 'v2/core/services/user';

export const useUpdateSearchDashboardAction = () => {
  const {
    id: userID,
    tenant: { id: tenantID, slug: tenantSlug },
  } = useGetUser();

  const searchQuery = useSearchStore(({ query }) => query);
  const searchDashboard = useSearchStore(({ data }) => data);
  const dispatch = useSearchStore(({ dispatch }) => dispatch);

  const { updateAnalysis, isLoading: isUpdatingAnalysis } = useUpdateAnalysis(
    {},
  );

  const searchWhereClause = useSearchAnalysesWhereClause();

  const eqlProps: Omit<EQLProps, 'sourceDetailsMap'> = {
    expressionType: ExpressionType.Records,
    whereClause: searchWhereClause,
    dateRange: searchQuery.duration as EQLProps['dateRange'],
  };

  const { eql: analysisEql } = useGetEQLExpression({
    tenantID,
    eqlProps,
  });

  const { updateSearchDashboard, isLoading: isUpdatingDashboard } =
    useUpdateSearchDashboard({
      searchDashboard: searchDashboard!,
    });

  const isLoading = isUpdatingAnalysis || isUpdatingDashboard;

  useFullscreenLoader({ isLoading });

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const onUpdateSearchDashboard = async () => {
    try {
      if (!searchDashboard) {
        return;
      }

      const searchDashboardAnalysis = searchDashboard.analyses[0];

      if (searchDashboardAnalysis) {
        await updateAnalysis({
          variables: {
            ...searchDashboardAnalysis,
            tenantID,
            scope: mapToScope(searchDashboardAnalysis.scope),
            editToken: searchDashboardAnalysis.editToken ?? '',
            createdBy: searchDashboardAnalysis.createdBy.id,
            context: {
              eqlExpression: analysisEql ?? '',
            },
          },
        });
      }

      const metadata = getSearchMetadataUrl({
        tenantSlug,
        ...searchQuery!,
        isSearchToFeedEnabled,
      });
      await updateSearchDashboard({
        variables: {
          ...searchDashboard,
          userID,
          metadata,
          tenantID,
          scope: mapToScope(searchDashboard.scope),
          editToken: searchDashboard.editToken ?? '',
          createdBy: userID,
        },
      });
      dispatch({
        type: SearchActionType.SET_UPDATE_QUERY,
        payload: searchQuery,
      });
      dispatch({
        type: SearchActionType.SET_DATA,
        payload: {
          metadata,
        },
      });
    } catch {
      // do nothing, already handled by useUpdateSearchDashboard
    }
  };

  return { onUpdateSearchDashboard, isLoading };
};
